import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { useUIStore } from '../../stores/ui-store';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';
import {
  BlockFormData,
  BlockCategory,
  BlockScope,
  DifficultyLevel,
  getBlockCategoryName,
  getBlockScopeName,
  getDifficultyLevelName,
  PlatformBlockDetailResponse,
  PlatformBlockSummaryResponse,
  BlockExerciseFormData,
  ExerciseOption
} from './types';
import {
  useCreatePlatformBlock,
  useUpdatePlatformBlock,
  usePlatformExerciseOptions,
  usePlatformEquipmentOptions,
  usePlatformBlockDetail
} from './api';

interface BlockFormProps {
  isOpen: boolean;
  onClose: () => void;
  block?: PlatformBlockDetailResponse | PlatformBlockSummaryResponse;
  mode: 'create' | 'edit';
}

const initialFormData: BlockFormData = {
  name: '',
  description: '',
  category: BlockCategory.WarmUp,
  difficulty: DifficultyLevel.Beginner,
  durationMinutes: 10,
  primaryEquipmentId: undefined,
  scope: BlockScope.Global,
  displayOrder: 1,
  tags: '',
  originalAuthor: '',
  isActive: true,
  exercises: [],
};

const initialExerciseData: BlockExerciseFormData = {
  exerciseId: 0,
  exerciseName: '',
  orderIndex: 1,
  durationSeconds: undefined,
  repetitions: undefined,
  springSettings: '',
  equipmentSetupId: undefined,
  notes: '',
  transitionSeconds: 15,
  isOptional: false,
};

export function BlockForm({ isOpen, onClose, block, mode }: BlockFormProps) {
  const [formData, setFormData] = useState<BlockFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [editingExerciseIndex, setEditingExerciseIndex] = useState<number | null>(null);
  const [currentExercise, setCurrentExercise] = useState<BlockExerciseFormData>(initialExerciseData);
  
  const { addNotification } = useUIStore();
  const createMutation = useCreatePlatformBlock();
  const updateMutation = useUpdatePlatformBlock();
  const { data: exerciseData } = usePlatformExerciseOptions();
  const { data: equipmentData } = usePlatformEquipmentOptions();
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Fetch detailed block data when editing
  const { data: detailedBlock, isLoading: isLoadingDetail } = usePlatformBlockDetail(
    mode === 'edit' && block ? block.id : 0
  );

  // Reset form when modal opens/closes or block changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'create') {
        setFormData(initialFormData);
      } else if (mode === 'edit' && block) {
        // For edit mode, prioritize detailed data but only populate if we have it or aren't loading
        if (detailedBlock) {
          // We have detailed data with exercises
          setFormData({
            name: detailedBlock.name,
            description: detailedBlock.description || '',
            category: detailedBlock.category,
            difficulty: detailedBlock.difficulty,
            durationMinutes: detailedBlock.durationMinutes,
            primaryEquipmentId: detailedBlock.primaryEquipmentId,
            scope: detailedBlock.scope,
            displayOrder: detailedBlock.displayOrder,
            tags: detailedBlock.tags || '',
            originalAuthor: detailedBlock.originalAuthor || '',
            isActive: detailedBlock.isActive,
            exercises: detailedBlock.exercises.map(ex => ({
              exerciseId: ex.exerciseId,
              exerciseName: ex.exerciseName,
              orderIndex: ex.orderIndex,
              durationSeconds: ex.durationSeconds,
              repetitions: ex.repetitions,
              springSettings: ex.springSettings || '',
              equipmentSetupId: ex.equipmentSetupId,
              notes: ex.notes || '',
              transitionSeconds: ex.transitionSeconds,
              isOptional: ex.isOptional,
            })),
          });
        } else if (!isLoadingDetail) {
          // We're not loading detailed data, so fall back to summary (no exercises)
          setFormData({
            name: block.name,
            description: block.description || '',
            category: block.category,
            difficulty: block.difficulty,
            durationMinutes: block.durationMinutes,
            primaryEquipmentId: 'primaryEquipmentId' in block ? block.primaryEquipmentId : undefined,
            scope: 'scope' in block ? block.scope : BlockScope.Global,
            displayOrder: block.displayOrder,
            tags: block.tags || '',
            originalAuthor: 'originalAuthor' in block ? block.originalAuthor || '' : '',
            isActive: block.isActive,
            exercises: [],
          });
        }
        // If isLoadingDetail is true, don't populate yet - wait for detailed data
      }
      setErrors({});
    }
  }, [isOpen, mode, block, detailedBlock, isLoadingDetail]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Block name is required';
    }

    if (formData.durationMinutes < 1) {
      newErrors.durationMinutes = 'Duration must be at least 1 minute';
    }

    if (formData.displayOrder < 1) {
      newErrors.displayOrder = 'Display order must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const exerciseRequests = formData.exercises.map(ex => ({
        exerciseId: ex.exerciseId,
        orderIndex: ex.orderIndex,
        durationSeconds: ex.durationSeconds || undefined,
        repetitions: ex.repetitions || undefined,
        springSettings: ex.springSettings.trim() || undefined,
        equipmentSetupId: ex.equipmentSetupId || undefined,
        notes: ex.notes.trim() || undefined,
        transitionSeconds: ex.transitionSeconds,
        isOptional: ex.isOptional,
      }));

      if (mode === 'create') {
        await createMutation.mutateAsync({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          category: formData.category,
          difficulty: formData.difficulty,
          durationMinutes: formData.durationMinutes,
          primaryEquipmentId: formData.primaryEquipmentId || undefined,
          scope: formData.scope,
          displayOrder: formData.displayOrder,
          tags: formData.tags.trim() || undefined,
          originalAuthor: formData.originalAuthor.trim() || undefined,
          exercises: exerciseRequests.length > 0 ? exerciseRequests : undefined,
        });

        addNotification({
          type: 'success',
          title: 'Block Created',
          message: `${formData.name} has been created successfully.`,
        });
      } else {
        if (!block) return;

        await updateMutation.mutateAsync({
          id: block.id,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          category: formData.category,
          difficulty: formData.difficulty,
          durationMinutes: formData.durationMinutes,
          primaryEquipmentId: formData.primaryEquipmentId || undefined,
          scope: formData.scope,
          displayOrder: formData.displayOrder,
          tags: formData.tags.trim() || undefined,
          originalAuthor: formData.originalAuthor.trim() || undefined,
          isActive: formData.isActive ?? true,
          exercises: exerciseRequests.length > 0 ? exerciseRequests : undefined,
        });

        addNotification({
          type: 'success',
          title: 'Block Updated',
          message: `${formData.name} has been updated successfully.`,
        });
      }

      onClose();
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: `Failed to ${mode === 'create' ? 'Create' : 'Update'} Block`,
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleInputChange = (field: keyof BlockFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddExercise = () => {
    setCurrentExercise({ ...initialExerciseData, orderIndex: formData.exercises.length + 1 });
    setEditingExerciseIndex(null);
    setShowExerciseModal(true);
  };

  const handleEditExercise = (index: number) => {
    setCurrentExercise({ ...formData.exercises[index] });
    setEditingExerciseIndex(index);
    setShowExerciseModal(true);
  };

  const handleSaveExercise = () => {
    const exercises = [...formData.exercises];
    
    if (editingExerciseIndex !== null) {
      // Edit existing exercise
      exercises[editingExerciseIndex] = { ...currentExercise };
    } else {
      // Add new exercise
      exercises.push({ ...currentExercise });
    }
    
    // Re-index exercises
    exercises.forEach((ex, index) => {
      ex.orderIndex = index + 1;
    });
    
    setFormData(prev => ({ ...prev, exercises }));
    setShowExerciseModal(false);
  };

  const handleRemoveExercise = (index: number) => {
    const exercises = formData.exercises.filter((_, i) => i !== index);
    // Re-index exercises
    exercises.forEach((ex, idx) => {
      ex.orderIndex = idx + 1;
    });
    setFormData(prev => ({ ...prev, exercises }));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const exercises = [...formData.exercises];
      const oldIndex = exercises.findIndex((_, index) => `exercise-${index}` === active.id);
      const newIndex = exercises.findIndex((_, index) => `exercise-${index}` === over?.id);

      const reorderedExercises = arrayMove(exercises, oldIndex, newIndex);
      
      // Re-index exercises
      reorderedExercises.forEach((ex, idx) => {
        ex.orderIndex = idx + 1;
      });
      
      setFormData(prev => ({ ...prev, exercises: reorderedExercises }));
    }
  };

  const handleMoveExercise = (index: number, direction: 'up' | 'down') => {
    const exercises = [...formData.exercises];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < exercises.length) {
      [exercises[index], exercises[newIndex]] = [exercises[newIndex], exercises[index]];
      // Re-index exercises
      exercises.forEach((ex, idx) => {
        ex.orderIndex = idx + 1;
      });
      setFormData(prev => ({ ...prev, exercises }));
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const exerciseOptions = exerciseData?.exercises || [];
  const equipmentOptions = equipmentData?.equipment || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <CardHeader>
          <CardTitle>
            {mode === 'create' ? 'Create New Block' : 'Edit Block'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {mode === 'edit' && isLoadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 mx-auto mb-4">
                  <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <p className="text-gray-600">Loading block details...</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex h-[80vh]">
              {/* Left Panel - Basic Fields (30%) */}
              <div className="w-[30%] p-6 border-r border-gray-200 overflow-y-auto">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Block Details</h3>

                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={errors.name ? 'border-red-500' : ''}
                      placeholder="Enter block name"
                      disabled={isLoading}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Duration */}
                  <div>
                    <label htmlFor="durationMinutes" className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (minutes) *
                    </label>
                    <Input
                      id="durationMinutes"
                      type="number"
                      min="1"
                      value={formData.durationMinutes}
                      onChange={(e) => handleInputChange('durationMinutes', parseInt(e.target.value) || 1)}
                      className={errors.durationMinutes ? 'border-red-500' : ''}
                      disabled={isLoading}
                    />
                    {errors.durationMinutes && (
                      <p className="text-red-500 text-xs mt-1">{errors.durationMinutes}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Enter block description"
                      disabled={isLoading}
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      {Object.values(BlockCategory)
                        .filter(value => typeof value === 'number')
                        .map((category) => (
                          <option key={category} value={category}>
                            {getBlockCategoryName(category as BlockCategory)}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Difficulty */}
                  <div>
                    <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-1">
                      Difficulty *
                    </label>
                    <select
                      id="difficulty"
                      value={formData.difficulty}
                      onChange={(e) => handleInputChange('difficulty', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      {Object.values(DifficultyLevel)
                        .filter(value => typeof value === 'number')
                        .map((difficulty) => (
                          <option key={difficulty} value={difficulty}>
                            {getDifficultyLevelName(difficulty as DifficultyLevel)}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Scope */}
                  <div>
                    <label htmlFor="scope" className="block text-sm font-medium text-gray-700 mb-1">
                      Scope *
                    </label>
                    <select
                      id="scope"
                      value={formData.scope}
                      onChange={(e) => handleInputChange('scope', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      {Object.values(BlockScope)
                        .filter(value => typeof value === 'number')
                        .map((scope) => (
                          <option key={scope} value={scope}>
                            {getBlockScopeName(scope as BlockScope)}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Primary Equipment */}
                  <div>
                    <label htmlFor="primaryEquipmentId" className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Equipment
                    </label>
                    <select
                      id="primaryEquipmentId"
                      value={formData.primaryEquipmentId || ''}
                      onChange={(e) => handleInputChange('primaryEquipmentId', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      <option value="">No specific equipment</option>
                      {equipmentOptions.map((equipment) => (
                        <option key={equipment.id} value={equipment.id}>
                          {equipment.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Display Order */}
                  <div>
                    <label htmlFor="displayOrder" className="block text-sm font-medium text-gray-700 mb-1">
                      Display Order *
                    </label>
                    <Input
                      id="displayOrder"
                      type="number"
                      min="1"
                      value={formData.displayOrder}
                      onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value) || 1)}
                      className={errors.displayOrder ? 'border-red-500' : ''}
                      disabled={isLoading}
                    />
                    {errors.displayOrder && (
                      <p className="text-red-500 text-xs mt-1">{errors.displayOrder}</p>
                    )}
                  </div>

                  {/* Tags */}
                  <div>
                    <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                      Tags
                    </label>
                    <Input
                      id="tags"
                      type="text"
                      value={formData.tags}
                      onChange={(e) => handleInputChange('tags', e.target.value)}
                      placeholder="e.g., strength, flexibility, core"
                      disabled={isLoading}
                    />
                  </div>

                  {/* Original Author */}
                  <div>
                    <label htmlFor="originalAuthor" className="block text-sm font-medium text-gray-700 mb-1">
                      Original Author
                    </label>
                    <Input
                      id="originalAuthor"
                      type="text"
                      value={formData.originalAuthor}
                      onChange={(e) => handleInputChange('originalAuthor', e.target.value)}
                      placeholder="Enter author name"
                      disabled={isLoading}
                    />
                  </div>

                  {/* Is Active (only for edit mode) */}
                  {mode === 'edit' && (
                    <div className="flex items-center space-x-2">
                      <input
                        id="isActive"
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                      <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                        Active
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel - Exercises (70%) */}
              <div className="flex-1 flex flex-col">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Exercises in Block</h3>
                    <Button
                      type="button"
                      onClick={handleAddExercise}
                      variant="outline"
                      disabled={isLoading}
                    >
                      Add Exercise
                    </Button>
                  </div>
                </div>

                <div className="flex-1 p-6 overflow-y-auto">
                  {formData.exercises.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-lg mb-2">No exercises added yet</p>
                      <p className="text-sm">Click "Add Exercise" above to start building your block</p>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext 
                        items={formData.exercises.map((_, index) => `exercise-${index}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-3">
                          {formData.exercises.map((exercise, index) => (
                            <SortableExerciseItem
                              key={`exercise-${index}`}
                              id={`exercise-${index}`}
                              exercise={exercise}
                              index={index}
                              isLoading={isLoading}
                              isFirst={index === 0}
                              isLast={index === formData.exercises.length - 1}
                              onMoveUp={() => handleMoveExercise(index, 'up')}
                              onMoveDown={() => handleMoveExercise(index, 'down')}
                              onEdit={() => handleEditExercise(index)}
                              onRemove={() => handleRemoveExercise(index)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>

                {/* Form Actions - Fixed at bottom of right panel */}
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <div className="flex justify-end space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="min-w-[80px]"
                    >
                      {isLoading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Exercise Modal */}
      {showExerciseModal && (
        <ExerciseModal
          exercise={currentExercise}
          exercises={exerciseOptions}
          onSave={handleSaveExercise}
          onCancel={() => setShowExerciseModal(false)}
          onChange={(field, value) => setCurrentExercise(prev => ({ ...prev, [field]: value }))}
        />
      )}
    </div>
  );
}

// Sortable Exercise Item Component
interface SortableExerciseItemProps {
  id: string;
  exercise: BlockExerciseFormData;
  index: number;
  isLoading: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: () => void;
  onRemove: () => void;
}

function SortableExerciseItem({
  id,
  exercise,
  index,
  isLoading,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onEdit,
  onRemove,
}: SortableExerciseItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-gray-50 rounded-md ${
        isDragging ? 'opacity-50 z-50' : ''
      }`}
    >
      <div className="flex items-center space-x-3 flex-1">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
          title="Drag to reorder"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="w-4 h-4"
          >
            <circle cx="3" cy="3" r="1.5" />
            <circle cx="3" cy="8" r="1.5" />
            <circle cx="3" cy="13" r="1.5" />
            <circle cx="8" cy="3" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="8" cy="13" r="1.5" />
            <circle cx="13" cy="3" r="1.5" />
            <circle cx="13" cy="8" r="1.5" />
            <circle cx="13" cy="13" r="1.5" />
          </svg>
        </div>

        {/* Exercise Info */}
        <div className="flex-1">
          <div className="font-medium text-gray-900">
            {exercise.orderIndex}. {exercise.exerciseName || `Exercise ${exercise.exerciseId}`}
          </div>
          <div className="text-sm text-gray-600">
            {exercise.durationSeconds && `${exercise.durationSeconds}s`}
            {exercise.repetitions && ` • ${exercise.repetitions} reps`}
            {exercise.springSettings && ` • ${exercise.springSettings}`}
            {exercise.isOptional && ` • Optional`}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onMoveUp}
          disabled={isFirst || isLoading}
          title="Move up"
        >
          ↑
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onMoveDown}
          disabled={isLast || isLoading}
          title="Move down"
        >
          ↓
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onEdit}
          disabled={isLoading}
        >
          Edit
        </Button>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          onClick={onRemove}
          disabled={isLoading}
        >
          Remove
        </Button>
      </div>
    </div>
  );
}

// Exercise selection and configuration modal
interface ExerciseModalProps {
  exercise: BlockExerciseFormData;
  exercises: ExerciseOption[];
  onSave: () => void;
  onCancel: () => void;
  onChange: (field: keyof BlockExerciseFormData, value: any) => void;
}

function ExerciseModal({ exercise, exercises, onSave, onCancel, onChange }: ExerciseModalProps) {
  const selectedExerciseOption = exercises.find(ex => ex.id === exercise.exerciseId);

  const handleExerciseSelect = (exerciseId: number) => {
    const selected = exercises.find(ex => ex.id === exerciseId);
    onChange('exerciseId', exerciseId);
    onChange('exerciseName', selected?.name || '');
    // Auto-fill defaults if available
    if (selected?.typicalDurationSeconds) {
      onChange('durationSeconds', selected.typicalDurationSeconds);
    }
    if (selected?.defaultReps) {
      onChange('repetitions', selected.defaultReps);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <Card className="w-full max-w-md max-h-[80vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Configure Exercise</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Exercise Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Exercise *
            </label>
            <select
              value={exercise.exerciseId || ''}
              onChange={(e) => handleExerciseSelect(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select an exercise...</option>
              {exercises.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.name} ({ex.category})
                </option>
              ))}
            </select>
          </div>

          {selectedExerciseOption && (
            <div className="p-3 bg-blue-50 rounded-md text-sm">
              <p><strong>Equipment:</strong> {selectedExerciseOption.primaryEquipmentName}</p>
              <p><strong>Difficulty:</strong> {selectedExerciseOption.difficulty}</p>
            </div>
          )}

          {/* Duration and Reps */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (seconds)
              </label>
              <Input
                type="number"
                min="1"
                value={exercise.durationSeconds || ''}
                onChange={(e) => onChange('durationSeconds', parseInt(e.target.value) || undefined)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Repetitions
              </label>
              <Input
                type="number"
                min="1"
                value={exercise.repetitions || ''}
                onChange={(e) => onChange('repetitions', parseInt(e.target.value) || undefined)}
              />
            </div>
          </div>

          {/* Spring Settings */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Spring Settings
            </label>
            <Input
              type="text"
              value={exercise.springSettings}
              onChange={(e) => onChange('springSettings', e.target.value)}
              placeholder="e.g., 2 Red, 1 Blue"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={exercise.notes}
              onChange={(e) => onChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Special instructions or modifications"
            />
          </div>

          {/* Transition Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transition Time (seconds)
            </label>
            <Input
              type="number"
              min="0"
              value={exercise.transitionSeconds}
              onChange={(e) => onChange('transitionSeconds', parseInt(e.target.value) || 15)}
            />
          </div>

          {/* Is Optional */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={exercise.isOptional}
              onChange={(e) => onChange('isOptional', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label className="text-sm font-medium text-gray-700">
              Optional exercise
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={!exercise.exerciseId}>
              Save Exercise
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}